import { Key, Clock, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

export const ApiSecurityAuthentication = () => {
  const jwtBestPractices = [
    {
      title: "Short Expiration Times",
      description: "Access tokens should expire in 15 minutes or less. Use refresh tokens for longer sessions.",
      icon: Clock,
    },
    {
      title: "Secure Token Storage",
      description: "Never store tokens in localStorage. Use httpOnly cookies or secure memory-only storage.",
      icon: ShieldCheck,
    },
    {
      title: "Token Rotation",
      description: "Implement refresh token rotation — each use invalidates the previous token.",
      icon: RefreshCw,
    },
    {
      title: "Strong Signing Keys",
      description: "Use RS256 or ES256 algorithms with keys of at least 2048 bits. Rotate keys regularly.",
      icon: Key,
    },
  ];

  const commonMistakes = [
    "Storing JWTs in localStorage (XSS vulnerable)",
    "Using weak secrets like 'secret123' for HS256",
    "Not validating token claims (iss, aud, exp)",
    "Passing tokens in URL query parameters",
    "Not implementing token revocation mechanisms",
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Authentication Best Practices
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Proper authentication is your first line of defense. Here's how to implement JWT-based 
        authentication that actually protects your APIs.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-chart-4" />
            JWT Best Practices
          </h3>
          <div className="space-y-4">
            {jwtBestPractices.map((practice, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border border-border p-4 hover:border-chart-4/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-chart-4/10 flex items-center justify-center shrink-0">
                    <practice.icon className="w-5 h-5 text-chart-4" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">{practice.title}</h4>
                    <p className="text-sm text-muted-foreground">{practice.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Common Authentication Mistakes
          </h3>
          <div className="bg-destructive/5 rounded-xl border border-destructive/20 p-6">
            <ul className="space-y-3">
              {commonMistakes.map((mistake, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-destructive/20 text-destructive text-sm font-medium flex items-center justify-center shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-foreground">{mistake}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 bg-card rounded-xl border border-border p-4">
            <h4 className="font-medium text-foreground mb-2">Pro Tip</h4>
            <p className="text-sm text-muted-foreground">
              Consider using OAuth 2.0 with PKCE for public clients (SPAs, mobile apps). It provides 
              better security than implicit flow and handles token management properly.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
